import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Base from '../components/Base';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, ExternalLink, Copy, Edit, Trash2, Eye,
  CheckCircle, AlertCircle, Package, ChevronDown, ArrowRight,
  Store, ShoppingBag, Sparkles, TrendingUp, Palette, Shield, Share2, Layout,
  Search, X, Filter, CheckSquare, Square, Layers,
} from 'lucide-react';
import BrandOnboarding from '../components/BrandOnboarding';
import API_BASE_URL from '../config/api';

import { devLog } from '../utils/devLog';
const API_BASE_URL_FOR_IMAGES = API_BASE_URL;

const HomePage = () => {
  const navigate = useNavigate();
  const { authToken, logoutUser, user } = useContext(AuthContext);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_active: false,
    expiry_date: null,
  });
  const [templateStatus, setTemplateStatus] = useState({ has_selection: false, temp_no: null });
  const [kycStatus, setKycStatus] = useState({ completed: false });
  const [slideIndexes, setSlideIndexes] = useState({});
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  const authAxios = useMemo(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
    },
  }), [authToken?.access]);

  const categories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.category).filter(Boolean))];
    return cats.sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    let result = products;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p => p.name?.toLowerCase().includes(q));
    }
    if (categoryFilter !== 'all') {
      result = result.filter(p => p.category === categoryFilter);
    }
    if (stockFilter !== 'all') {
      const inStock = stockFilter === 'in_stock';
      result = result.filter(p => p.in_stock === inStock);
    }
    return result;
  }, [products, searchQuery, categoryFilter, stockFilter]);

  const hasActiveFilters = searchQuery.trim() || categoryFilter !== 'all' || stockFilter !== 'all';

  const clearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('all');
    setStockFilter('all');
  };

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await authAxios.get('/get_subscription_status/');
      setSubscriptionStatus(response.data);
    } catch (err) {
      devLog.error('Failed to fetch subscription status:', err);
    }
  }, [authAxios]);

  const fetchTemplateStatus = useCallback(async () => {
    try {
      const response = await authAxios.get('/template/selection');
      if (response.data?.success) {
        setTemplateStatus({
          has_selection: response.data.has_selection || false,
          temp_no: response.data.template?.temp_no || null,
        });
      }
    } catch (err) {
      devLog.error('Failed to fetch template status:', err);
    }
  }, [authAxios]);

  const fetchKycStatus = useCallback(async () => {
    try {
      const response = await authAxios.get('/designer/profile/');
      const profile = response.data;
      setKycStatus({
        completed: !!(profile?.pan_number && profile?.bank_account_number && profile?.ifsc_code),
      });
    } catch (err) {
      devLog.error('Failed to fetch KYC status:', err);
    }
  }, [authAxios]);

  const fetchProducts = useCallback(async () => {
    if (!authToken?.access) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await authAxios.get('/products/');

      if (response.data && Array.isArray(response.data.products)) {
        setProducts(response.data.products);
        const initialSlideIndexes = {};
        response.data.products.forEach((product, idx) => {
          initialSlideIndexes[idx] = 1;
        });
        setSlideIndexes(initialSlideIndexes);
      } else {
        setProducts([]);
      }
    } catch (err) {
      devLog.error('Error fetching products:', err.response || err);
      setError('Failed to load products. Please try again later.');
      if (err.response?.status === 401) {
        Swal.fire({
          title: 'Session Expired',
          text: 'Please login again',
          icon: 'warning',
          confirmButtonColor: '#d45614',
        }).then(() => {
          logoutUser();
          navigate('/login');
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, authToken?.access, navigate, logoutUser]);

  useEffect(() => {
    if (authToken) {
      fetchSubscriptionStatus();
      fetchProducts();
      fetchTemplateStatus();
      fetchKycStatus();

      const onboardingCompleted = localStorage.getItem('onboarding_completed');
      if (!onboardingCompleted) {
        setTimeout(() => {
          setShowOnboarding(true);
        }, 500);
      }
    } else {
      navigate('/login');
    }
  }, [authToken, navigate, fetchSubscriptionStatus, fetchProducts, fetchTemplateStatus, fetchKycStatus]);

  const toggleStock = async (productId) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire({ title: 'Subscription Required', text: 'Please subscribe to perform this action.', icon: 'error', confirmButtonColor: '#d45614' });
      return;
    }
    try {
      const response = await authAxios.post(`/toggle_stock/${productId}/`);
      if (response.data.success) {
        setProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.id === productId
              ? { ...product, in_stock: response.data.in_stock }
              : product
          )
        );
        Swal.fire({
          title: 'Updated!',
          text: `Product is now ${response.data.in_stock ? 'In Stock' : 'Out of Stock'}.`,
          icon: 'success',
          confirmButtonColor: '#d45614',
        });
      }
    } catch (err) {
      devLog.error('Error toggling stock:', err.response || err);
      Swal.fire({ title: 'Error', text: 'Failed to toggle stock status.', icon: 'error', confirmButtonColor: '#d45614' });
    }
  };

  const confirmDelete = (productId) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire({ title: 'Subscription Required', text: 'Please subscribe to perform this action.', icon: 'error', confirmButtonColor: '#d45614' });
      return;
    }
    Swal.fire({
      title: 'Delete this design?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await authAxios.delete(`/delete_product/${productId}/`);
          Swal.fire({ title: 'Deleted!', text: 'The product has been removed.', icon: 'success', confirmButtonColor: '#d45614' });
          setProducts((prevProducts) =>
            prevProducts.filter((product) => product.id !== productId)
          );
        } catch (err) {
          devLog.error('Error deleting product:', err.response || err);
          Swal.fire({ title: 'Error', text: 'Failed to delete the product.', icon: 'error', confirmButtonColor: '#d45614' });
        }
      }
    });
  };

  const handleCopyUrl = (url) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire({ title: 'Subscription Required', text: 'Please subscribe to perform this action.', icon: 'error', confirmButtonColor: '#d45614' });
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      const Toast = Swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 3000, timerProgressBar: true });
      Toast.fire({ icon: "success", title: "Product URL copied!" });
    }).catch(err => {
      devLog.error('Failed to copy URL:', err);
    });
  };

  const handleEdit = (productId) => {
    if (!subscriptionStatus.is_active) {
      Swal.fire({ title: 'Subscription Required', text: 'Please subscribe to perform this action.', icon: 'error', confirmButtonColor: '#d45614' });
      return;
    }
    navigate(`/EditProduct/${productId}`);
  };

  const handleUploadProduct = (e) => {
    if (!subscriptionStatus.is_active) {
      e.preventDefault();
      Swal.fire({ title: 'Subscription Required', text: 'Please subscribe to upload products.', icon: 'error', confirmButtonColor: '#d45614' });
    }
  };

  const handleClone = async (productId) => {
    try {
      const res = await authAxios.post(`/clone_product/${productId}/`);
      if (res.data.success) {
        const Toast = Swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 2000, timerProgressBar: true });
        Toast.fire({ icon: "success", title: `"${res.data.product.name}" created` });
        fetchProducts();
      }
    } catch (err) {
      Swal.fire({ title: 'Error', text: 'Failed to clone product.', icon: 'error', confirmButtonColor: '#d45614' });
    }
  };

  const handleBulkAction = async (action) => {
    const ids = [...selectedIds];
    const labels = { delete: 'delete', in_stock: 'mark as In Stock', out_of_stock: 'mark as Out of Stock' };
    const result = await Swal.fire({
      title: `${action === 'delete' ? 'Delete' : 'Update'} ${ids.length} product${ids.length > 1 ? 's' : ''}?`,
      text: `This will ${labels[action]} the selected products.`,
      icon: action === 'delete' ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonColor: action === 'delete' ? '#EF4444' : '#d45614',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, proceed',
    });
    if (!result.isConfirmed) return;
    try {
      const res = await authAxios.post('/bulk_product_action/', { product_ids: ids, action });
      if (res.data.success) {
        const Toast = Swal.mixin({ toast: true, position: "top-end", showConfirmButton: false, timer: 2000, timerProgressBar: true });
        Toast.fire({ icon: "success", title: `${res.data.affected} product${res.data.affected > 1 ? 's' : ''} updated` });
        setSelectedIds(new Set());
        setSelectMode(false);
        fetchProducts();
      }
    } catch (err) {
      Swal.fire({ title: 'Error', text: 'Bulk action failed.', icon: 'error', confirmButtonColor: '#d45614' });
    }
  };

  const toggleSelect = (productId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  // ─── Slideshow Logic (preserved) ──────────────────────────
  const plusSlides = (n, productIndex) => {
    setSlideIndexes((prev) => {
      const newIndexes = { ...prev };
      const currentProductSlideIndex = newIndexes[productIndex] || 1;
      const slides = document.querySelectorAll(`#slideshow-container-${productIndex} .mySlides`);
      if (!slides || slides.length === 0) return newIndexes;
      let nextSlideIndex = currentProductSlideIndex + n;
      if (nextSlideIndex > slides.length) nextSlideIndex = 1;
      if (nextSlideIndex < 1) nextSlideIndex = slides.length;
      newIndexes[productIndex] = nextSlideIndex;
      return newIndexes;
    });
  };

  const currentSlide = (n, productIndex) => {
    setSlideIndexes((prev) => ({ ...prev, [productIndex]: n }));
  };

  const showSlides = useCallback((productIndex) => {
    const slides = document.querySelectorAll(`#slideshow-container-${productIndex} .mySlides`);
    const dots = document.querySelectorAll(`#slideshow-container-${productIndex} .dot`);
    if (!slides || slides.length === 0) return;

    slides.forEach((slide) => (slide.style.display = 'none'));
    dots.forEach((dot) => {
      dot.classList.remove('bg-[#d45614]');
      dot.classList.add('bg-white/50');
    });

    const currentProductSlideIndex = slideIndexes[productIndex] || 1;
    if (slides[currentProductSlideIndex - 1]) {
      slides[currentProductSlideIndex - 1].style.display = 'block';
    }
    if (dots.length > 0 && dots[currentProductSlideIndex - 1]) {
      dots[currentProductSlideIndex - 1].classList.remove('bg-white/50');
      dots[currentProductSlideIndex - 1].classList.add('bg-[#d45614]');
    }
  }, [slideIndexes]);

  useEffect(() => {
    products.forEach((_, index) => {
      if (slideIndexes[index] !== undefined) {
        showSlides(index);
      }
    });
  }, [products, slideIndexes, showSlides]);

  // ─── Helpers ──────────────────────────────────────────────
  const storeUrl = user?.username ? `https://${user.username}.vasstr.com` : '#';
  const storeUrlDisplay = user?.username ? `${user.username}.vasstr.com` : 'yourstore.vasstr.com';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { y: 16, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } },
  };

  const checklistSteps = [
    { icon: <Sparkles className="w-4 h-4" />, title: 'Start your free trial', desc: 'Activate 14-day free access to all features', completed: subscriptionStatus.is_active, to: '/subscription', actionLabel: 'Activate' },
    { icon: <Plus className="w-4 h-4" />, title: 'Upload your first design', desc: 'Add at least one product to your store', completed: products.length > 0, to: '/UploadProduct', actionLabel: 'Upload' },
    { icon: <Edit className="w-4 h-4" />, title: 'Complete your brand profile', desc: 'Add brand name, logo, and business address', completed: false, to: '/Profile', actionLabel: 'Edit Profile' },
    { icon: <Package className="w-4 h-4" />, title: `Build your catalog (${Math.min(products.length, 5)}/5)`, desc: 'Add 5+ designs for better customer selection', completed: products.length >= 5, to: '/UploadProduct', actionLabel: 'Upload More' },
    { icon: <Shield className="w-4 h-4" />, title: 'Complete KYC', desc: 'Verify your identity to receive payouts from orders', completed: kycStatus.completed, to: '/designer-onboarding', actionLabel: 'Start KYC' },
    { icon: <Layout className="w-4 h-4" />, title: 'Choose a store template', desc: 'Pick a design for your storefront', completed: templateStatus.has_selection, to: '/TemplateThemes', actionLabel: 'Browse Themes' },
    { icon: <Palette className="w-4 h-4" />, title: 'Customize your store', desc: 'Edit pages, colors, fonts, and images in the visual editor', completed: false, to: templateStatus.temp_no ? `/template-builder/${templateStatus.temp_no}` : '/TemplateThemes', actionLabel: 'Open Editor' },
    { icon: <Share2 className="w-4 h-4" />, title: 'Share your store link', desc: 'Promote your brand — share with customers on WhatsApp & Instagram', action: 'copy' },
    { icon: <ShoppingBag className="w-4 h-4" />, title: 'Explore Order Book', desc: 'Track offline orders, events & measurements — all in one place', completed: false, to: '/orderbook', actionLabel: 'Open Order Book' },
  ];

  const completedCount = checklistSteps.filter((s) => s.completed).length;

  // ─── Loading ──────────────────────────────────────────────
  if (isLoading) {
    return (
      <Base Content={
        <div className="flex flex-col items-center justify-center h-screen bg-white gap-3">
          <div className="w-10 h-10 border-3 border-[#d45614] border-t-transparent rounded-full animate-spin" />
          <p className="text-base text-gray-500 font-fell">Loading your dashboard...</p>
        </div>
      } />
    );
  }

  // ─── Error ────────────────────────────────────────────────
  if (error) {
    return (
      <Base Content={
        <div className="flex flex-col items-center justify-center h-screen bg-white px-4 gap-4">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-gray-600 text-center font-fell text-lg">{error}</p>
          <button
            onClick={() => { fetchProducts(); fetchSubscriptionStatus(); }}
            className="text-sm font-medium px-5 py-2.5 rounded-[9px] bg-[#d45614] text-white hover:bg-[#a8430e] transition-colors"
          >
            Try Again
          </button>
        </div>
      } />
    );
  }

  // ═══════════════════════════════════════════════════════════
  // NEW USER: 0 products
  // ═══════════════════════════════════════════════════════════
  if (products.length === 0) {
    return (
      <>
        <BrandOnboarding
          isOpen={showOnboarding}
          onClose={() => setShowOnboarding(false)}
          username={user?.username || 'yourstore'}
        />
        <Base
          Content={
            <div className="min-h-screen bg-white">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14"
              >
                {/* Welcome Hero */}
                <motion.div variants={itemVariants} className="text-center mb-10 sm:mb-14">
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#F4ECE5] flex items-center justify-center mx-auto mb-6 shadow-md border border-[#C9A882]/20"
                  >
                    <Store className="w-10 h-10 sm:w-12 sm:h-12 text-[#d45614]" />
                  </motion.div>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C1810] font-fell mb-4">
                    Welcome to Your Store
                  </h1>
                  <p className="text-lg sm:text-xl text-gray-500 max-w-xl mx-auto leading-relaxed">
                    Your fashion store is live at{' '}
                    <a
                      href={storeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#d45614] font-semibold hover:underline"
                    >
                      {storeUrlDisplay}
                    </a>
                  </p>
                </motion.div>

                {/* Subscription Warning */}
                {!subscriptionStatus.is_active && (
                  <motion.div
                    variants={itemVariants}
                    className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-5 py-4 mb-8 bg-amber-50 border border-amber-200 rounded-[9px]"
                  >
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <p className="text-sm text-amber-800 flex-1">
                      Subscribe to start managing your store and upload products.
                    </p>
                    <Link
                      to="/subscription"
                      className="text-sm font-semibold text-[#d45614] hover:text-[#a8430e] transition-colors whitespace-nowrap"
                    >
                      Subscribe Now &rarr;
                    </Link>
                  </motion.div>
                )}

                {/* Getting Started Steps */}
                <motion.div variants={itemVariants} className="mb-10 sm:mb-14">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-[#d45614] flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-[#2C1810] font-fell">
                      Getting Started
                    </h2>
                  </div>

                  <div className="space-y-3">
                    {checklistSteps.map((step, i) => (
                      <motion.div
                        key={i}
                        variants={itemVariants}
                        className={`flex items-start gap-4 px-5 py-4 rounded-[9px] border transition-all duration-200 ${
                          step.completed
                            ? 'bg-green-50 border-green-200'
                            : 'bg-white border-gray-200 hover:border-[#C9A882]/60 hover:shadow-sm'
                        }`}
                      >
                        {/* Step number / check */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          step.completed
                            ? 'bg-green-500 text-white'
                            : 'bg-[#F4ECE5] text-[#2C1810]'
                        }`}>
                          {step.completed ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <span className="text-sm font-semibold">{i + 1}</span>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm sm:text-base font-semibold ${
                            step.completed ? 'text-green-800' : 'text-[#2C1810]'
                          }`}>
                            {step.title}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                            {step.desc}
                          </p>
                        </div>

                        {/* Action */}
                        {step.to && !step.completed && (
                          <Link
                            to={step.to}
                            onClick={step.to === '/UploadProduct' ? handleUploadProduct : undefined}
                            className="flex-shrink-0"
                          >
                            <span className="text-xs sm:text-sm font-semibold text-[#d45614] hover:text-[#a8430e] transition-colors flex items-center gap-1">
                              {step.actionLabel} <ArrowRight className="w-3.5 h-3.5" />
                            </span>
                          </Link>
                        )}
                        {step.action === 'copy' && (
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(storeUrl);
                              Swal.fire({ icon: 'success', title: 'Copied!', text: 'Store URL copied', timer: 1500, showConfirmButton: false });
                            }}
                            className="flex-shrink-0 text-xs sm:text-sm font-semibold text-[#d45614] hover:text-[#a8430e] transition-colors flex items-center gap-1"
                          >
                            Copy Link <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {step.completed && (
                          <span className="text-xs font-medium text-green-600 flex-shrink-0">Done</span>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Primary CTA */}
                <motion.div variants={itemVariants} className="text-center mb-10">
                  <Link to="/UploadProduct" onClick={handleUploadProduct}>
                    <motion.button
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-[#d45614] text-white font-semibold px-10 py-4 rounded-[9px] text-base sm:text-lg shadow-lg hover:bg-[#a8430e] hover:shadow-xl transition-all duration-200 inline-flex items-center gap-3"
                    >
                      <Plus className="w-5 h-5 sm:w-6 sm:h-6" />
                      Upload Your First Design
                    </motion.button>
                  </Link>
                  <p className="text-sm text-gray-400 mt-3">Takes only 2-3 minutes</p>
                </motion.div>

                {/* Benefits */}
                <motion.div
                  variants={itemVariants}
                  className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-[#F4ECE5] rounded-[9px] p-6 sm:p-8 border border-[#C9A882]/20"
                >
                  {[
                    { icon: <Palette className="w-6 h-6" />, title: 'Showcase Creativity', desc: 'Display your unique designs to the world' },
                    { icon: <ShoppingBag className="w-6 h-6" />, title: 'Start Earning', desc: 'Receive and manage orders directly' },
                    { icon: <TrendingUp className="w-6 h-6" />, title: 'Grow Your Brand', desc: 'Build your independent fashion business' },
                  ].map((benefit, idx) => (
                    <div key={idx} className="text-center py-2">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-3 shadow-sm text-[#d45614]">
                        {benefit.icon}
                      </div>
                      <p className="font-semibold text-[#2C1810] text-sm mb-1 font-fell">{benefit.title}</p>
                      <p className="text-xs text-gray-500">{benefit.desc}</p>
                    </div>
                  ))}
                </motion.div>

                {/* Tutorial link */}
                <motion.div variants={itemVariants} className="text-center mt-8">
                  <button
                    onClick={() => { localStorage.removeItem('onboarding_completed'); setShowOnboarding(true); }}
                    className="text-sm text-[#d45614] hover:text-[#a8430e] transition-colors font-medium flex items-center gap-1 mx-auto"
                  >
                    <Sparkles className="w-4 h-4" />
                    Watch Tutorial
                  </button>
                </motion.div>
              </motion.div>
            </div>
          }
        />
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // RETURNING USER: 1+ products
  // ═══════════════════════════════════════════════════════════
  return (
    <>
      <BrandOnboarding
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        username={user?.username || 'yourstore'}
      />
      <Base
        Content={
          <div className="min-h-screen bg-white">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8"
            >
              {/* ─── Dashboard Header Card ─────────────────── */}
              <motion.div
                variants={itemVariants}
                className="bg-[#F4ECE5] rounded-[9px] border border-[#C9A882]/30 p-5 sm:p-7 mb-6 shadow-sm"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Left: Brand Info */}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-[#d45614] flex items-center justify-center shadow-sm">
                        <Store className="w-5 h-5 text-white" />
                      </div>
                      <h1 className="text-2xl sm:text-3xl font-bold text-[#2C1810] font-fell">
                        My Store
                      </h1>
                    </div>
                    <a
                      href={storeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#d45614] hover:text-[#a8430e] transition-colors font-medium inline-flex items-center gap-1.5 ml-[52px]"
                    >
                      {storeUrlDisplay} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>

                  {/* Right: Quick Actions */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <Link to="/UploadProduct" onClick={handleUploadProduct}>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`bg-[#d45614] text-white text-sm font-medium px-5 py-2.5 rounded-[9px] transition-all shadow-sm hover:bg-[#a8430e] hover:shadow-md inline-flex items-center gap-2 ${
                          !subscriptionStatus.is_active ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                      >
                        <Plus className="w-4 h-4" />
                        Upload Product
                      </motion.button>
                    </Link>
                    <a href={storeUrl} target="_blank" rel="noopener noreferrer">
                      <button className="bg-white border border-[#C9A882]/40 text-[#2C1810] text-sm font-medium px-5 py-2.5 rounded-[9px] hover:border-[#C9A882] hover:shadow-sm transition-all inline-flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Preview Store
                      </button>
                    </a>
                    <button
                      onClick={() => { localStorage.removeItem('onboarding_completed'); setShowOnboarding(true); }}
                      className="bg-white border border-gray-200 text-gray-500 text-sm font-medium px-4 py-2.5 rounded-[9px] hover:border-gray-300 transition-all inline-flex items-center gap-1.5"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      Tutorial
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* ─── Stats Cards ────────────────────────────── */}
              <motion.div
                variants={containerVariants}
                className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6"
              >
                {[
                  {
                    label: 'Total Designs',
                    value: products.length,
                    sub: 'in your catalog',
                    icon: <Package className="w-5 h-5 sm:w-6 sm:h-6" />,
                    color: '#d45614',
                    bgColor: '#FEF3ED',
                  },
                  {
                    label: 'In Stock',
                    value: products.filter((p) => p.in_stock).length,
                    sub: 'available now',
                    icon: <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" />,
                    color: '#10B981',
                    bgColor: '#D1FAE5',
                  },
                  {
                    label: 'Out of Stock',
                    value: products.filter((p) => !p.in_stock).length,
                    sub: 'need restocking',
                    icon: <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6" />,
                    color: products.filter((p) => !p.in_stock).length > 0 ? '#EF4444' : '#6B7280',
                    bgColor: products.filter((p) => !p.in_stock).length > 0 ? '#FEE2E2' : '#F3F4F6',
                  },
                  {
                    label: 'Store Status',
                    value: subscriptionStatus.is_active ? 'Active' : 'Inactive',
                    sub: subscriptionStatus.is_active ? 'Ready to sell' : 'Subscribe to activate',
                    icon: subscriptionStatus.is_active ? <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" /> : <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6" />,
                    color: subscriptionStatus.is_active ? '#10B981' : '#EF4444',
                    bgColor: subscriptionStatus.is_active ? '#D1FAE5' : '#FEE2E2',
                  },
                ].map((stat, idx) => (
                  <motion.div
                    key={idx}
                    variants={itemVariants}
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                    className="bg-white rounded-[9px] border border-gray-200 p-4 sm:p-5 shadow-sm hover:shadow-md transition-all duration-200"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <p className="text-xs sm:text-sm text-gray-500 font-medium">{stat.label}</p>
                      <div
                        className="w-8 h-8 sm:w-9 sm:h-9 rounded-[9px] flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: stat.bgColor, color: stat.color }}
                      >
                        {stat.icon}
                      </div>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold text-[#2C1810] font-fell">{stat.value}</p>
                    <p className="text-xs text-gray-400 mt-1">{stat.sub}</p>
                  </motion.div>
                ))}
              </motion.div>

              {/* ─── Subscription Warning ───────────────────── */}
              {!subscriptionStatus.is_active && (
                <motion.div
                  variants={itemVariants}
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-5 py-4 mb-6 border border-red-200 bg-red-50 rounded-[9px]"
                >
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-red-800">Subscription Inactive</p>
                    <p className="text-xs text-red-600">Subscribe to manage your store, upload products, and start selling.</p>
                  </div>
                  <Link to="/subscription">
                    <button className="bg-[#d45614] text-white text-sm font-medium px-5 py-2 rounded-[9px] hover:bg-[#a8430e] transition-colors whitespace-nowrap shadow-sm">
                      Subscribe Now
                    </button>
                  </Link>
                </motion.div>
              )}

              {/* ─── Getting Started Checklist (Collapsible) ── */}
              <motion.div
                variants={itemVariants}
                className="mb-6 bg-white border border-gray-200 rounded-[9px] shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setChecklistOpen(!checklistOpen)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#d45614] flex items-center justify-center">
                      <TrendingUp className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-sm sm:text-base font-semibold text-[#2C1810] font-fell">
                      Getting Started
                    </span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full font-medium">
                      {completedCount}/{checklistSteps.length}
                    </span>
                  </div>
                  <motion.div
                    animate={{ rotate: checklistOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false}>
                  {checklistOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-gray-100 px-5 pb-4 pt-2 space-y-2">
                        {checklistSteps.map((step, i) => (
                          <div
                            key={i}
                            className={`flex items-center gap-3 px-4 py-3 rounded-[9px] transition-all duration-150 ${
                              step.completed
                                ? 'bg-green-50 border border-green-100'
                                : 'hover:bg-gray-50 border border-transparent'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                              step.completed
                                ? 'bg-green-500 text-white'
                                : 'bg-[#F4ECE5] text-[#2C1810]'
                            }`}>
                              {step.completed ? (
                                <CheckCircle className="w-3.5 h-3.5" />
                              ) : (
                                <span className="text-xs font-semibold">{i + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${step.completed ? 'text-green-700' : 'text-[#2C1810]'}`}>
                                {step.title}
                              </p>
                            </div>
                            {step.to && !step.completed && (
                              <Link to={step.to} onClick={step.to === '/UploadProduct' ? handleUploadProduct : undefined}>
                                <span className="text-xs font-medium text-[#d45614] hover:text-[#a8430e] transition-colors whitespace-nowrap">
                                  {step.actionLabel} &rarr;
                                </span>
                              </Link>
                            )}
                            {step.action === 'copy' && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(storeUrl);
                                  Swal.fire({ icon: 'success', title: 'Copied!', text: 'Store URL copied', timer: 1500, showConfirmButton: false });
                                }}
                                className="text-xs font-medium text-[#d45614] hover:text-[#a8430e] transition-colors whitespace-nowrap"
                              >
                                Copy Link &rarr;
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* ─── Catalog Header + Filters ─────────────────── */}
              <motion.div variants={itemVariants} className="mb-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl sm:text-2xl font-bold text-[#2C1810] font-fell">
                      Your Designs
                    </h2>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
                      {hasActiveFilters ? `${filteredProducts.length} of ${products.length}` : products.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
                      className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-[9px] border transition-all ${
                        selectMode
                          ? 'bg-[#d45614] text-white border-[#d45614]'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <CheckSquare className="w-3.5 h-3.5" />
                      {selectMode ? 'Cancel' : 'Select'}
                    </button>
                  </div>
                </div>

                {/* Filter Bar */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                  {/* Search */}
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-[9px] focus:outline-none focus:border-[#d45614] focus:ring-1 focus:ring-[#d45614]/20 bg-white"
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                        <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>
                  {/* Category */}
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="text-sm border border-gray-200 rounded-[9px] px-3 py-2 bg-white focus:outline-none focus:border-[#d45614] w-full sm:w-auto sm:min-w-[140px]"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {/* Stock */}
                  <select
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                    className="text-sm border border-gray-200 rounded-[9px] px-3 py-2 bg-white focus:outline-none focus:border-[#d45614] w-full sm:w-auto sm:min-w-[120px]"
                  >
                    <option value="all">All Stock</option>
                    <option value="in_stock">In Stock</option>
                    <option value="out_of_stock">Out of Stock</option>
                  </select>
                  {/* Clear Filters */}
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="flex items-center gap-1 text-xs font-medium text-[#d45614] hover:text-[#a8430e] px-2 py-2 whitespace-nowrap"
                    >
                      <X className="w-3.5 h-3.5" />
                      Clear
                    </button>
                  )}
                </div>

                {/* Select Mode Actions */}
                {selectMode && selectedIds.size > 0 && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-[9px] border border-gray-200">
                    <div className="flex items-center gap-2 mb-2 sm:mb-0">
                      <span className="text-xs font-semibold text-gray-700">
                        {selectedIds.size} selected
                      </span>
                      <button
                        onClick={() => {
                          const allIds = new Set(filteredProducts.map(p => p.id));
                          setSelectedIds(prev => prev.size === allIds.size ? new Set() : allIds);
                        }}
                        className="text-xs text-[#d45614] hover:text-[#a8430e] font-medium"
                      >
                        {selectedIds.size === filteredProducts.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => handleBulkAction('in_stock')}
                        className="text-xs font-medium px-3 py-1.5 rounded-[9px] bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 active:bg-green-200 transition-colors"
                      >
                        In Stock
                      </button>
                      <button
                        onClick={() => handleBulkAction('out_of_stock')}
                        className="text-xs font-medium px-3 py-1.5 rounded-[9px] bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 active:bg-orange-200 transition-colors"
                      >
                        Out of Stock
                      </button>
                      <button
                        onClick={() => handleBulkAction('delete')}
                        className="text-xs font-medium px-3 py-1.5 rounded-[9px] bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 active:bg-red-200 transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                )}
                {selectMode && selectedIds.size === 0 && (
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => setSelectedIds(new Set(filteredProducts.map(p => p.id)))}
                      className="text-xs text-[#d45614] hover:text-[#a8430e] font-medium"
                    >
                      Select All ({filteredProducts.length})
                    </button>
                  </div>
                )}
              </motion.div>

              {/* No results message */}
              {hasActiveFilters && filteredProducts.length === 0 && (
                <motion.div variants={itemVariants} className="text-center py-12 mb-6">
                  <Filter className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 font-medium">No products match your filters</p>
                  <button onClick={clearFilters} className="text-xs text-[#d45614] hover:text-[#a8430e] font-medium mt-2">
                    Clear all filters
                  </button>
                </motion.div>
              )}

              {/* ─── Product Grid — Portrait Cards ──────────── */}
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 mb-10"
              >
                {/* Add New Design Card */}
                <motion.div variants={itemVariants}>
                  <Link
                    to="/UploadProduct"
                    onClick={handleUploadProduct}
                    className={`block ${!subscriptionStatus.is_active ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <motion.div
                      whileHover={subscriptionStatus.is_active ? { y: -4, transition: { duration: 0.2 } } : {}}
                      className="border-2 border-dashed border-[#C9A882]/50 rounded-[9px] overflow-hidden hover:border-[#d45614]/40 transition-all duration-200 bg-[#F4ECE5]/30"
                    >
                      <div className="aspect-[3/4] flex items-center justify-center">
                        <div className="text-center">
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#d45614] flex items-center justify-center mx-auto mb-3 shadow-md"
                          >
                            <Plus className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                          </motion.div>
                          <p className="text-sm sm:text-base font-semibold text-[#2C1810] font-fell">
                            Add Design
                          </p>
                          <p className="text-xs text-gray-400 mt-1">Upload new product</p>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>

                {/* Product Cards */}
                {filteredProducts.map((product, index) => {
                  const imageUrls = [product.img1, product.img2, product.img3, product.img4, product.img5, product.img6]
                    .filter(Boolean)
                    .map((imgUrl) => {
                      if (imgUrl.startsWith('http://') || imgUrl.startsWith('https://') || imgUrl.startsWith('/media/')) {
                        return imgUrl;
                      }
                      return `${API_BASE_URL_FOR_IMAGES}${imgUrl}`;
                    });

                  return (
                    <motion.div
                      key={product.id}
                      variants={itemVariants}
                      whileHover={{ y: -4, transition: { duration: 0.2 } }}
                      className="group bg-white rounded-[9px] border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      {/* Image — Portrait 3:4 */}
                      <div
                        id={`slideshow-container-${index}`}
                        className="relative aspect-[3/4] overflow-hidden bg-gray-100"
                      >
                        {/* Slide Arrows */}
                        {imageUrls.length > 1 && (
                          <>
                            <button
                              className="absolute left-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200 shadow-sm hover:bg-white hover:scale-110"
                              onClick={() => plusSlides(-1, index)}
                            >
                              <svg className="w-3.5 h-3.5 text-[#2C1810]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 18l-6-6 6-6" />
                              </svg>
                            </button>
                            <button
                              className="absolute right-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-200 shadow-sm hover:bg-white hover:scale-110"
                              onClick={() => plusSlides(1, index)}
                            >
                              <svg className="w-3.5 h-3.5 text-[#2C1810]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 18l6-6-6-6" />
                              </svg>
                            </button>
                          </>
                        )}

                        {/* Images */}
                        {imageUrls.length > 0 ? (
                          imageUrls.map((img, idx) => (
                            <div key={idx} className="mySlides hidden w-full h-full">
                              <img
                                src={img}
                                alt={`${product.name} ${idx + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/400x533/F4ECE5/2C1810?text=No+Image'; }}
                              />
                            </div>
                          ))
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-[#F4ECE5]">
                            <Package className="w-10 h-10 text-[#C9A882]" />
                          </div>
                        )}

                        {/* Dots */}
                        {imageUrls.length > 1 && (
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                            {imageUrls.map((_, i) => (
                              <button
                                key={i}
                                className={`dot w-2 h-2 rounded-full transition-all duration-200 ${
                                  (slideIndexes[index] || 1) === i + 1
                                    ? 'bg-[#d45614] w-5'
                                    : 'bg-white/50'
                                }`}
                                onClick={() => currentSlide(i + 1, index)}
                              />
                            ))}
                          </div>
                        )}

                        {/* Select Checkbox */}
                        {selectMode && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleSelect(product.id); }}
                            className="absolute top-2 left-2 z-20"
                          >
                            {selectedIds.has(product.id) ? (
                              <CheckSquare className="w-5 h-5 text-[#d45614]" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                            )}
                          </button>
                        )}

                        {/* Out of Stock Badge */}
                        {!product.in_stock && (
                          <div className={`absolute ${selectMode ? 'top-9' : 'top-2'} left-2 bg-red-500 text-white px-2.5 py-1 rounded-full text-[10px] font-bold z-10 shadow-sm`}>
                            Out of Stock
                          </div>
                        )}

                        {/* Action Overlay — always visible on mobile, hover on desktop */}
                        <div className="absolute top-2 right-2 z-10 flex flex-col gap-1.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={() => handleEdit(product.id)}
                            disabled={!subscriptionStatus.is_active}
                            className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-white active:scale-95 sm:hover:scale-110 transition-all"
                            title="Edit"
                          >
                            <Edit className="w-3.5 h-3.5 text-[#2C1810]" />
                          </button>
                          <button
                            onClick={() => handleCopyUrl(product.external_url)}
                            disabled={!subscriptionStatus.is_active}
                            className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-white active:scale-95 sm:hover:scale-110 transition-all"
                            title="Copy URL"
                          >
                            <Copy className="w-3.5 h-3.5 text-[#2C1810]" />
                          </button>
                          <a
                            href={product.external_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-white active:scale-95 sm:hover:scale-110 transition-all ${
                              !subscriptionStatus.is_active ? 'pointer-events-none opacity-50' : ''
                            }`}
                            title="Preview"
                          >
                            <Eye className="w-3.5 h-3.5 text-[#2C1810]" />
                          </a>
                          <button
                            onClick={() => handleClone(product.id)}
                            disabled={!subscriptionStatus.is_active}
                            className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-white active:scale-95 sm:hover:scale-110 transition-all"
                            title="Duplicate"
                          >
                            <Layers className="w-3.5 h-3.5 text-[#2C1810]" />
                          </button>
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className="p-3 sm:p-4">
                        <h3
                          className="text-sm font-semibold text-[#2C1810] truncate font-fell"
                          title={product.name}
                        >
                          {product.name}
                        </h3>
                        <div className="flex items-baseline gap-2 mt-1.5">
                          <span className="text-base font-bold text-[#2C1810]">
                            ₹{parseFloat(product.price).toLocaleString('en-IN')}
                          </span>
                          {product.Discountedprice && (
                            <span className="text-xs text-green-600 font-semibold">
                              ₹{parseFloat(product.Discountedprice).toLocaleString('en-IN')}
                            </span>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                          <button
                            onClick={() => toggleStock(product.id)}
                            disabled={!subscriptionStatus.is_active}
                            className={`flex-1 text-xs font-medium py-2 rounded-[9px] transition-all duration-150 ${
                              !subscriptionStatus.is_active
                                ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400'
                                : product.in_stock
                                ? 'bg-[#d45614] text-white hover:bg-[#a8430e]'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {product.in_stock ? 'In Stock' : 'Out of Stock'}
                          </button>
                          <button
                            onClick={() => confirmDelete(product.id)}
                            disabled={!subscriptionStatus.is_active}
                            className={`px-3 py-2 rounded-[9px] border border-gray-200 transition-all duration-150 ${
                              subscriptionStatus.is_active
                                ? 'text-gray-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50'
                                : 'opacity-50 cursor-not-allowed text-gray-300'
                            }`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </motion.div>
          </div>
        }
      />
    </>
  );
};

export default HomePage;
